import React, { useEffect, useState } from 'react';
import {
  DecisionForm,
  HiredForm,
  MoveToInterviewForm,
  MoveToStages,
  RejectForm as RejectStageForm,
  StagesOptions,
} from 'domain/recruiting/recruitingBoard/shared';
import { Stages } from 'domain/shared/candidate';
import { useShowContext } from 'framework/show';
import { CreateForm, EnumDropdown } from 'framework/form';
import { ModalsWithAdditionalRequirements } from 'domain/shared/newVacancy';
import { useCandidateService } from 'domain/shared/candidate';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import Box from '@mui/material/Box';
import { Modal } from 'framework/ui/ui-builder';

interface EditCandidateInterviewStepProps {
  synchronizeStep: any;
  activeStep: Stages;
  vacancyIdForHiredStep: string;
}

const EditCandidateInterviewStep: React.FC<EditCandidateInterviewStepProps> = ({
  activeStep,
  synchronizeStep,
  vacancyIdForHiredStep,
}) => {
  const modalMap: Partial<ModalsWithAdditionalRequirements> = {
    [Stages.Interview]: MoveToInterviewForm,
    [Stages.Hired]: HiredForm,
    [Stages.Decision]: DecisionForm,
    [Stages.PendingRejection]: RejectStageForm,
  };

  const { data } = useShowContext();
  const { changeCandidateApplicationPiplineStep } = useCandidateService();

  const [ActiveModal, setActiveModal] = useState<React.ComponentType<any> | null>(null);
  const [dataForUpdateStep, setDataForUpdateStep] = useState<any>(null);
  const [entityObj, setEntityObj] = useState<any>(null);
  const [errorStepUpdating, setErrorStepUpdating] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      if (data.lastActiveApplication) {
        setEntityObj(data.lastActiveApplication);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeStepHandler = async (values: FormFieldsData) => {
    setDataForUpdateStep(values);

    const ActiveModalComponent = modalMap[values.EditCandidateInterviewStep as Stages];

    if (ActiveModalComponent) {
      setActiveModal(() => ActiveModalComponent);
    } else {
      changeStepHandlerStep2(values);
    }
  };

  const changeStepWithRequirementsHandler = async (values: FormFieldsData) => {
    changeStepHandlerStep2(dataForUpdateStep, values);
  };

  const changeStepHandlerStep2 = async (values: FormFieldsData, additionalValues?: FormFieldsData) => {
    if (typeof values?.EditCandidateInterviewStep !== 'undefined') {
      const stageKey = values.EditCandidateInterviewStep as keyof typeof MoveToStages;
      const updatedData = await changeCandidateApplicationPiplineStep(
        data.lastActiveApplication.id,
        MoveToStages[stageKey] as Stages,
        additionalValues,
      );
      if (updatedData !== false) {
        synchronizeStep(stageKey);
        setErrorStepUpdating(false);
      } else {
        setErrorStepUpdating(true);
      }
    }
  };

  const closeDialog = () => {
    setActiveModal(null);
  };

  return (
    <Box
      sx={{
        '.MuiBox-root': {
          marginBottom: 0,
          width: '150px',
          '& .dropdown__control': {
            backgroundColor: 'primary.main',
            color: 'common.white',
            padding: '0px 4px',
            minHeight: 'initial',
            '& .dropdown__value-container': {
              minHeight: 'initial',
            },
          },
          '& .dropdown__placeholder': {
            color: errorStepUpdating ? 'text.secondary' : 'common.white',
          },
          '& .dropdown__indicator svg': {
            fill: 'var(--mui-palette-common-white) !important',
          },
          '& .dropdown__value': {
            minHeight: 'initial',
          },
          '& .dropdown__input-container': {
            padding: 0,
            margin: 0,
            '& .dropdown__input': {
              color: 'var(--mui-palette-common-white) !important',
              caretColor: 'var(--mui-palette-primary-main)',
            },
          },
          '& .dropdown__menu': {
            marginTop: 0,
            '& .dropdown__menu-list': {
              backgroundColor: 'primary.main',
              borderRadius: '3px',
              '& .dropdown__option': {
                color: 'common.white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
            },
          },
        },
      }}
    >
      <EnumDropdown
        placeholder="Move to"
        placeholderOnly
        options={StagesOptions}
        source="EditCandidateInterviewStep"
        label=""
        onChangeCallback={changeStepHandler}
        disabled={errorStepUpdating}
        validationCallback={() => {}}
        clearValidationCallback={() => {}}
      />
      {ActiveModal && (
        <Modal
          isOpen
          key="modal-not-strict"
          modalSize="sm"
          backdrop="static"
          title="Additional info required"
          onToggle={closeDialog}
        >
          <CreateForm
            submitFormFunc={formData => changeStepWithRequirementsHandler(formData)}
            afterSubmit={{
              execute: () => {
                setActiveModal(null);
              },
            }}
          >
            <ActiveModal
              handleCancelClick={closeDialog}
              sourceStage={activeStep}
              vacancyGroupId={vacancyIdForHiredStep}
              entity={entityObj}
            />
          </CreateForm>
        </Modal>
      )}
    </Box>
  );
};

export default EditCandidateInterviewStep;
